import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetFtpDetails } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import { useLocation } from 'react-router-dom';
import { ConfirmationDialog, ProjectDialog } from '@zz2/zz2-ui';
import { IFtpDetail } from '../../../@types/model/masterData/ftpDetail/ftpDetail';
import { IFtpDetailFormValues } from '../../../@types/model/masterData/ftpDetail/ftpDetailFormValues';
import FtpDetailHttpService from '../../../services/http/masterData/ftpDetailHttpService';
import FtpDetailForm from './form/ftpDetailForm';
import { IApiError } from '../../../@types/other';
import { setFtpDetailMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import FtpDetailModelHelper from '../../../@types/model/masterData/ftpDetail/ftpDetailModelHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import UserHelper from '../../../services/helper/userHelper';
import { CustomTable } from '../../../components/table/CustomTableWrapper';

const FtpDetailScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const ftpDetailData = useAppSelector(state => state.masterData.ftpDetails);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedFtpDetail, setSelectedFtpDetail] = useState<IFtpDetail | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingFtpDetail, setDeletingFtpDetail] = useState<IFtpDetail | null>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        loadData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const loadData = async () => {
        // checks if indexedDB is available.
        const isIndexedDBAvailable = !!self.indexedDB;

        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await FtpDetailHttpService.getFtpDetailData();
                dispatch(dataSetFtpDetails(res.data));
            } catch (e) {
                generalShowErrorSnackbar('An error occurred while loading FTP details.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IFtpDetailFormValues) => {
        try {
            setIsLoading(true);

            const upsert = FtpDetailModelHelper.createUpsert(values);
            const res = await FtpDetailHttpService.addOrUpdateFtpDetail(upsert);

            const newFtpDetailList = upsertArrayElement(ftpDetailData, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetFtpDetails(newFtpDetailList));
            await setFtpDetailMasterDataIndexedDB(newFtpDetailList);

            if (selectedFtpDetail) {
                generalShowSuccessSnackbar('FTP detail updated successfully.');
            } else {
                generalShowSuccessSnackbar('FTP detail added successfully.');
            }

            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating FTP detail data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeFtpDetail = async () => {
        if (deletingFtpDetail) {
            const newFtpDetail = { ...deletingFtpDetail, isActive: false };
            setIsLoading(true);

            try {
                const res = await FtpDetailHttpService.deleteFtpDetail(newFtpDetail.id);

                const newFtpDetailList = upsertArrayElement(ftpDetailData, res.data, x => x.id === res.data.id) ?? [];
                dispatch(dataSetFtpDetails(newFtpDetailList));
                await setFtpDetailMasterDataIndexedDB(newFtpDetailList);

                generalShowSuccessSnackbar('FTP detail deleted successfully.');
            } catch (error : unknown) {
                const apiError = error as IApiError;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting FTP detail.');
            } finally {
                closeDeletePopup();
                setIsLoading(false);
            }
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedFtpDetail(null);
    };

    const edit = (selectedRow : IFtpDetail) => {
        setSelectedFtpDetail(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IFtpDetail) : void => {
        setDeletingFtpDetail(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingFtpDetail(null);
    };

    const deleteButtonTooltip = (row : IFtpDetail) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('FTP Detail', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return FtpDetailModelHelper.createFormValues(selectedFtpDetail);
        }
    }, [selectedFtpDetail, isDialogOpen]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading}>
            <Card className={'fdc hfill'}>
                <CustomTable<IFtpDetail>
                    
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || isLoading}
                    addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('FTP Detail', RightGrantsEnum.Add) : 'Add'}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('FTP Detail', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : IFtpDetail) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                    enableSorting
                    enableFiltering
                    fitWidthToPage
                    enablePagination
                    columns={[
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Address', field: 'address', enableFiltering: true, enableSorting: true },
                        { title: 'Port', field: 'port', enableFiltering: true, enableSorting: true },
                        { title: 'Path', field: 'path', enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={ftpDetailData}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={190}
                    isActive={(row : IFtpDetail) => row.isActive}
                />
            </Card>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedFtpDetail ? 'Edit FTP Detail' : 'Add FTP Detail'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <FtpDetailForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate FTP Detail'}
                open={!!deletingFtpDetail}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeFtpDetail}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default FtpDetailScreen;
